import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { STAGE_LABEL, flagDestinations, seesWorkSteps, type Stage } from '@/domain';
import { useKit, useKitHistory, useQcPass, useQcRework } from '@/application/usecases/useKits';
import { useCaseRecord } from '@/application/usecases/useArchive';
import { formatDuration } from '@/application/timer/timerStore';
import { useSession } from '@/application/usecases/useSession';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { Decision } from '@/ui/primitives/Decision';
import { reportCommandFailure } from '@/features/shell/reportCommandFailure';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { useOfferUndo } from '@/features/shell/useOfferUndo';
import { CheckIcon, GoBackIcon, ReworkIcon } from '@/ui/icons';
import { CaseRecordView } from '../archive/CaseRecordView';
import { AddNote } from '../queue/AddNote';
import { StagePickerDialog } from '../queue/StagePickerDialog';
import { WorkTimeline } from './WorkTimeline';
import { PageLoading } from '@/ui/feedback/Loading';
import styles from './FinalCheckScreen.module.css';

/**
 * Pass or fail one case. Section 5.2, `openQCRoute()` and `qcPass`.
 *
 * Two outcomes and no others. Pass advances the kit to Ready. Send to rework
 * picks the department that fixes it and REQUIRES a note: the specification
 * says a picker plus a note, and the prototype's optional note loses on
 * behaviour. The kit is marked rework and stays flagged until it passes here.
 *
 * Send for rework works like assigning a case. Section 8 of the request of
 * 5 October 2026: the technician is chosen from the list, with departments and
 * how many cases each holds; the reason is required; the sheet confirms before
 * it sends; and the toast after it offers Undo. The technician chosen is told,
 * and the rework and its reason are in the case's Lab Notes.
 *
 * Pass is one tap, so management is asked before it is sent: the request of
 * 5 October 2026 (third), section 2. A department supervisor is a technician
 * and passes as before, at once; who is asked goes by the role, never by the
 * permission the two share. Undo stays on the toast either way.
 */
export function QcCaseScreen() {
  const { kitId } = useParams<{ kitId: string }>();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { data: kit, isPending, isError } = useKit(kitId);
  /*
   * The whole case, by its job order number, which is the same call the record
   * route makes. One more query on a screen that already makes three, and it
   * buys every field the record has instead of a hand-built subset.
   */
  const record = useCaseRecord(kit?.jobOrderNo);

  // The server refuses either way, section 7.10 item 5. This keeps the screen
  // from offering a decision it knows will be turned down.
  const mayDecide = session?.permissions.includes('finalcheck') === true;
  /*
   * The work on the tray: its own bench entries, which the server gives to a
   * holder of the case log and to whoever decides here, and refuses everybody
   * else. Asked for only by somebody it will answer. The request of
   * 7 October 2026, section 4.
   */
  const seesWork = seesWorkSteps(
    session?.permissions.includes('seeCaseLog') === true,
    mayDecide,
  );
  const work = useKitHistory(seesWork ? kitId : undefined);

  const pass = useQcPass();
  const rework = useQcRework();
  const offerUndo = useOfferUndo();
  const ask = useConfirmAction();
  const [picking, setPicking] = useState(false);

  if (isPending) return <PageLoading label={strings.common.loadingKit} />;
  if (isError || kit === undefined) return <p>{strings.errors.server}</p>;

  /*
   * Passing as a department supervisor rather than as management. Section 11.2
   * of the request of 20 September 2026: the log distinguishes the two, so the
   * person deciding should see which decision they are making.
   */
  const asSupervisor =
    session?.user.isSupervisor === true && session.role !== 'management';
  const inFinalCheck = kit.stage === 'finalcheck';

  return (
    <div>
      <button className={styles.back} onClick={() => void navigate('/qc')}>
        <GoBackIcon /> {strings.finalCheck.back}
      </button>

      {/*
        The three facts that change the decision and are not on the record,
        because the record is not a decision screen. Section 4.4 of the request
        of 16 September 2026.
      */}
      <div className={styles.summary}>
        <span>
          {strings.finalCheck.waiting} <b>{formatDuration(kit.minutesInStage * 60)}</b>
        </span>
        {kit.rework && <span className={styles.flagRework}>{strings.finalCheck.returned}</span>}
        {kit.urgent && <span className={styles.flagUrgent}>{strings.orders.urgent}</span>}
      </div>

      {/*
        When the lab received the case, how long it has had it, and who worked it
        in order, with every flag and rework marked: what the decision is about,
        so it stands above the record. The request of 7 October 2026, section 4.
      */}
      {work.data !== undefined && (
        <WorkTimeline jobOrderNo={kit.jobOrderNo} receivedAt={kit.receivedAt} history={work.data} />
      )}

      {/*
        The WHOLE case, the same component the record shows: the photographs, the
        history, the times, the proof of work, the bridges, the dates, the doctor
        and the patient and the value. This screen used to draw its own subset of
        ten fields, so the manager DECIDING whether a case passes saw less than
        the manager who merely opened it from Orders. Section 4 of the request of
        16 September 2026.
      */}
      {record.data !== undefined && <CaseRecordView record={record.data} />}

      {/* And the one thing the record has no business holding: a place to write. */}
      <div className={styles.card}>
        <div className={styles.cardBody}>
          <AddNote kitId={kit.id} />
        </div>
      </div>

      <div className={styles.actions}>
        <div className={styles.actionsLead}>
          {strings.finalCheck.label}
          <b>{STAGE_LABEL[kit.stage]}</b>
        </div>

        <div className={styles.actionsButtons}>
          {!inFinalCheck && <span>{strings.finalCheck.notInFinalCheck}</span>}

          {/* Held: no decision until it is released. Section 9 of the review of 2 October 2026. */}
          {inFinalCheck && kit.held && <span>{strings.job.heldNotice}</span>}

          {mayDecide && inFinalCheck && !kit.held && (
            /*
             * Pass and Send to rework answer one question, so they move
             * together or not at all. Section 9 of the request of
             * 16 September 2026.
             */
            <Decision label={strings.finalCheck.decisionLabel}>
              <Button
                icon={<ReworkIcon />}
                onClick={() => setPicking(true)}
                disabled={rework.isPending}
              >
                {strings.finalCheck.sendToRework}
              </Button>
              <Button
                variant="primary"
                icon={<CheckIcon />}
                disabled={pass.isPending}
                onClick={() =>
                  ask(
                    strings.confirm.passFinalCheck(kit.jobOrderNo),
                    () =>
                      pass.mutateAsync(kit.id).then(
                        (passed) => {
                          offerUndo(`${kit.jobOrderNo} ${strings.finalCheck.passed}`, passed.undo);
                          void navigate('/qc');
                        },
                        (error: Error) => reportCommandFailure(error, () => void navigate('/qc')),
                      ),
                    { title: strings.finalCheck.pass, confirmLabel: strings.finalCheck.pass },
                  )
                }
              >
                {strings.finalCheck.pass}
              </Button>
            </Decision>
          )}

          {/*
            In what capacity. Section 11.2 of the request of 20 September 2026: a
            manager passing a case and a department supervisor passing one are two
            different decisions, and the log says which. This is where the person
            deciding sees which one they are making.
          */}
          {mayDecide && inFinalCheck && asSupervisor && (
            <span className={styles.capacity}>{strings.finalCheck.passingAsSupervisor}</span>
          )}
        </div>
      </div>

      {picking && (
        <StagePickerDialog
          title={`${strings.finalCheck.reworkTitle} ${kit.jobOrderNo}`}
          lead={strings.finalCheck.reworkLead}
          confirmLabel={strings.finalCheck.reworkConfirm}
          // Intake is not offered, and neither is Ready: reaching Ready is what
          // this screen decides.
          destinations={flagDestinations('finalcheck')}
          statement={(to, person) =>
            strings.confirm.rework(kit.jobOrderNo, to === null ? null : STAGE_LABEL[to], person)
          }
          requireNote
          busy={rework.isPending}
          // The case, so the sheet lists who works the department chosen.
          kitId={kit.id}
          onCancel={() => setPicking(false)}
          onConfirm={(toStage: Stage, note: string, assignToUserId: string | null) =>
            rework.mutate(
              { kitId: kit.id, toStage, note, assignToUserId },
              {
                onSuccess: (sent) => {
                  setPicking(false);
                  offerUndo(
                    `${kit.jobOrderNo} ${strings.finalCheck.sentToRework} ${STAGE_LABEL[toStage]} ${strings.finalCheck.forRework}`,
                    sent.undo,
                    'neutral',
                  );
                  void navigate('/qc');
                },
                onError: (error: Error) =>
                  reportCommandFailure(error, () => void navigate('/qc')),
              },
            )
          }
        />
      )}
    </div>
  );
}

